import React, { memo, useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import translationKeys from '@translationKeys'
import { Nullable } from '@types'

import ChipList, { ChipListProps } from '@components/ChipList'
import ConfirmPopup from '@components/ConfirmPopup'
import { CustomTooltip } from '@components/CustomTooltip'
import InputWithTitleSection from '@components/InputWithTitleSection'
import { CssTruncation } from '@components/TruncateLines/index.styles'
import savedSearchApi from '@modules/savedSearch/api'
import { SavedSearch, SavedSearchAlertsFrequency } from '@modules/savedSearch/types'
import { MAX_PROSPECTS_TO_ADD_TO_SHORTLIST } from '@modules/search/constants'
import { SearchRequestBody, useSearchParamsForSavedSearch } from '@modules/search/hooks/useSearchProfiles'
import AddIcon from '@mui/icons-material/Add'
import { Typography } from '@mui/material'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Popover from '@mui/material/Popover'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import { useFeatureFlags } from '@shared/hooks/useFeatureFlags'
import { useNotification } from '@shared/hooks/useNotification'
import { numberWithCommas } from '@shared/utils'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { colors } from '@theme/variables'

import AddProspectsToShortlistPopover from '../../AddProspectToShortlistPopover'
import { AddToShortlistButtonIconWrapper, AddToShortlistButtonText, AddToShortlistButtonWrapper } from '../index.styles'
import { SaveSearchBtn, SavedSearchContainer, SearchString, Title } from './ResultChips.styles'

export interface ResultChipsProps {
  chips: ChipListProps['items']
  onDelete: ChipListProps['onDelete']
  resultCount: number
  isFilterApplied?: boolean
  searchString?: string
  appliedSavedSearchName?: string
  searchBody?: SearchRequestBody
}

const ResultChips = ({
  chips,
  onDelete,
  resultCount = 0,
  searchString,
  isFilterApplied,
  appliedSavedSearchName,
  searchBody,
}: ResultChipsProps) => {
  const { t } = useTranslation()
  const { showNotification } = useNotification()
  const [isSavePopoverOpen, setIsSavePopoverOpen] = useState(false)
  const saveButtonRef = useRef(null)
  const queryClient = useQueryClient()

  const [savedSearchName, setSavedSearchName] = useState('')
  const [addToShortlistPopoverAnchor, setAddToShortlistPopoverAnchor] = useState<Nullable<HTMLElement>>(null)
  const [alertOption, setSavedSearchAlertsFrequency] = useState<SavedSearchAlertsFrequency>(
    SavedSearchAlertsFrequency.NO_NOTIFICATION,
  )
  const [isConfirmRewritePopupShown, setIsConfirmRewritePopupShown] = useState(false)

  const { hasAccessToSavedSearch, hasAccessToShortlists } = useFeatureFlags()
  const location = useLocation()
  const [confirmExitPopupCloseAction, setConfirmExitPopupCloseAction] = useState<{ close: () => void } | null>(null)

  const handlePopoverClose = useCallback(() => {
    if (savedSearchName.trim()) {
      setConfirmExitPopupCloseAction({ close: () => setIsSavePopoverOpen(false) })
    } else {
      setIsSavePopoverOpen(false)
      setSavedSearchName('')
    }
  }, [savedSearchName])

  const handleCancelEdit = useCallback(() => {
    confirmExitPopupCloseAction?.close?.()
    setConfirmExitPopupCloseAction(null)
    setSavedSearchName('')
  }, [confirmExitPopupCloseAction])

  const renderLabel = useCallback(
    () => (
      <Box display="flex" alignItems="center">
        <Box mr="14px">
          {appliedSavedSearchName ? (
            <Box display="flex" alignItems="center">
              <Typography variant="h2" color={colors.pink} sx={{ marginRight: '8px', maxWidth: '172px' }}>
                <CustomTooltip title={appliedSavedSearchName?.length > 16 ? appliedSavedSearchName : ''} placement="top">
                  <CssTruncation>{appliedSavedSearchName}</CssTruncation>
                </CustomTooltip>
              </Typography>

              <Typography variant="h2">{`(${numberWithCommas(resultCount)})`}</Typography>
            </Box>
          ) : (
            <Title variant="h2">{t(translationKeys.SEARCH_RESULT_TITLE, { resultCount: numberWithCommas(resultCount) })}</Title>
          )}
        </Box>

        <Box mr="14px">
          <SearchString variant="body1">{Boolean(searchString) && `"${searchString}"`}</SearchString>
        </Box>
      </Box>
    ),
    [resultCount, searchString, appliedSavedSearchName],
  )

  const searchParams = useSearchParamsForSavedSearch()

  const { mutate: postSavedSearch } = useMutation({
    mutationFn: savedSearchApi.postSavedSearch.fn,
    onSuccess: (resultItem: SavedSearch) => {
      showNotification(t(translationKeys.YOU_HAVE_SAVED_YOUR_SEARCH), 'success', 'Search is saved')
      setIsSavePopoverOpen(false)
      setSavedSearchToDefault()

      queryClient.setQueryData<SavedSearch[]>([savedSearchApi.getSavedSearches.key], (oldData = []) => {
        return [resultItem, ...oldData]
      })
    }, // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      if (error?.response?.data?.message === 'Field must be unique: name') {
        setIsSavePopoverOpen(false)
        setIsConfirmRewritePopupShown(true)
      } else if (error?.response?.data?.message?.startsWith('You have reached your limit')) {
        showNotification('You have reached your limit of 25 email notifications for saved searches.', 'error')
      } else {
        showNotification('Something went wrong', 'error')
      }
    },
  })

  const { mutate: updateSavedSearchByName } = useMutation({
    mutationFn: savedSearchApi.updateSavedSearchByName.fn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [savedSearchApi.getSavedSearches.key] })

      showNotification(t(translationKeys.YOU_HAVE_SAVED_YOUR_SEARCH), 'success', 'Search is saved')
      setIsSavePopoverOpen(false)
      setIsConfirmRewritePopupShown(false)
      setSavedSearchToDefault()
    },
    onError: () => {
      showNotification('Something went wrong', 'error')
      setIsConfirmRewritePopupShown(false)
    },
  })

  const setSavedSearchToDefault = () => {
    setSavedSearchAlertsFrequency(SavedSearchAlertsFrequency.NO_NOTIFICATION)
    setIsConfirmRewritePopupShown(false)
    setSavedSearchName('')
  }
  const handleAlertOptionChange = (event: SelectChangeEvent<SavedSearchAlertsFrequency>) => {
    const selectedValue = event.target.value as SavedSearchAlertsFrequency
    setSavedSearchAlertsFrequency(selectedValue)
  }

  const handleSaveSearch = () => {
    const savedSearchNameStr = savedSearchName.trim()

    if (savedSearchNameStr) {
      const alertsEnabled = alertOption !== SavedSearchAlertsFrequency.NO_NOTIFICATION
      const alertsFrequency = alertOption

      postSavedSearch({
        name: savedSearchNameStr,
        searchQuery: location.search,
        alertsEnabled,
        alertsFrequency: alertsEnabled ? alertsFrequency : SavedSearchAlertsFrequency.DAILY,
        searchParams,
      })
    }
  }
  const handleConfirmUpdateSearch = () => {
    const savedSearchNameStr = savedSearchName?.trim()

    if (savedSearchNameStr) {
      const alertsEnabled = alertOption !== SavedSearchAlertsFrequency.NO_NOTIFICATION
      const alertsFrequency = alertOption

      updateSavedSearchByName({
        name: savedSearchNameStr,
        searchQuery: location.search,
        alertsEnabled,
        alertsFrequency: alertsEnabled ? alertsFrequency : SavedSearchAlertsFrequency.DAILY,
      })
    }
  }

  const isAddProspectsToShortlistButtonDisabled = resultCount > MAX_PROSPECTS_TO_ADD_TO_SHORTLIST
  const addProspectsToShortlistTooltipText = useMemo(
    () =>
      hasAccessToShortlists && isAddProspectsToShortlistButtonDisabled
        ? t(translationKeys.ADD_ALL_PROSPECTS_TO_SHORTLIST_BUTTON_TOOLTIP)
        : '',
    [resultCount, hasAccessToShortlists],
  )

  return (
    <>
      <Box display="flex" alignItems="center">
        <ChipList
          items={chips}
          onDelete={onDelete}
          renderLabel={renderLabel}
          renderAdditionalItems={() => {
            if (
              (!hasAccessToSavedSearch && !hasAccessToShortlists) ||
              !(searchString || isFilterApplied) ||
              appliedSavedSearchName
            ) {
              // eslint-disable-next-line react/jsx-no-useless-fragment
              return <></>
            }
            const isAddProspectsToShortlistsDisabled = resultCount > MAX_PROSPECTS_TO_ADD_TO_SHORTLIST || resultCount === 0
            return (
              <>
                {hasAccessToSavedSearch && (
                  <SavedSearchContainer>
                    <SaveSearchBtn
                      ref={saveButtonRef}
                      onClick={() => setIsSavePopoverOpen(true)}
                      size="small"
                      variant="contained"
                    >
                      {t(translationKeys.SAVE_SEARCH_BUTTON)}
                    </SaveSearchBtn>
                  </SavedSearchContainer>
                )}
                {hasAccessToShortlists && (
                  <CustomTooltip width="350px" placement="top" title={addProspectsToShortlistTooltipText}>
                    <AddToShortlistButtonWrapper
                      $disabled={isAddProspectsToShortlistsDisabled}
                      onClick={(e: React.MouseEvent) =>
                        !isAddProspectsToShortlistsDisabled && setAddToShortlistPopoverAnchor(e.currentTarget as HTMLElement)
                      }
                    >
                      <AddToShortlistButtonText>{t(translationKeys.ADD_ALL_PROSPECTS_TO_SHORTLIST)}</AddToShortlistButtonText>
                      <AddToShortlistButtonIconWrapper>
                        <AddIcon fontSize="small" />
                      </AddToShortlistButtonIconWrapper>
                    </AddToShortlistButtonWrapper>
                  </CustomTooltip>
                )}
              </>
            )
          }}
        />
      </Box>
      <Popover
        open={isSavePopoverOpen}
        anchorEl={saveButtonRef.current}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Box p={2} style={{ width: '270px' }}>
          <InputWithTitleSection
            value={savedSearchName}
            title={t(translationKeys.SAVE_SEARCH_TITLE)}
            label={t(translationKeys.SEARCH_NAME_LABEL)}
            showMaxLengthMessage={false}
            maxLength={50}
            onChange={setSavedSearchName}
            onCancel={() => {
              setIsSavePopoverOpen(false)
              setSavedSearchToDefault()
              return true
            }}
            onSubmit={() => {
              handleSaveSearch()
            }}
            submitButtonText={t(translationKeys.SAVE)}
          >
            <Box mt={2} display="flex" alignItems="center" justifyContent="space-between">
              <Box flexGrow={1}>
                <FormControl fullWidth>
                  <InputLabel id="alert-select-label">{t(translationKeys.SEARCH_FORM_ALERT)}</InputLabel>
                  <Select
                    labelId="alert-select-label"
                    value={alertOption}
                    label={t(translationKeys.SEARCH_FORM_LABEL)}
                    onChange={handleAlertOptionChange}
                  >
                    <MenuItem value={SavedSearchAlertsFrequency.NO_NOTIFICATION}>
                      {t(translationKeys.SEARCH_NO_NOTIFICATION)}
                    </MenuItem>
                    <MenuItem value={SavedSearchAlertsFrequency.DAILY}>{t(translationKeys.SEARCH_MENU_DAILY)}</MenuItem>
                    <MenuItem value={SavedSearchAlertsFrequency.WEEKLY}>{t(translationKeys.SEARCH_MENU_WEEKLY)}</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </InputWithTitleSection>
        </Box>
      </Popover>
      {!!isConfirmRewritePopupShown && (
        <ConfirmPopup
          id="confirm-rewriting-existing-saved-search-popup"
          isOpen
          width={225}
          title={t(translationKeys.NAME_ALREADY_EXISTS)}
          text={t(translationKeys.DO_YOU_WANT_TO_REWRITE_SAVE)}
          cancelBtnText={t(translationKeys.CANCEL_BUTTON)}
          confirmBtnText={t(translationKeys.CONFIRM_BUTTON)}
          onCancel={() => {
            setIsConfirmRewritePopupShown(false)
            setIsSavePopoverOpen(true)
          }}
          onConfirm={handleConfirmUpdateSearch}
        />
      )}
      {confirmExitPopupCloseAction && (
        <ConfirmPopup
          id="unsaved-settings-confirm-popup"
          isOpen
          width={294}
          contentWidth={180}
          title={t(translationKeys.UNSAVED_SETTINGS)}
          text={t(translationKeys.YOU_HAVENOT_SAVED_CHANGES)}
          cancelBtnText={t(translationKeys.CONTINUE_EDITING)}
          confirmBtnText={t(translationKeys.CLOSE_ANYWAY)}
          onCancel={() => setConfirmExitPopupCloseAction(null)}
          onConfirm={handleCancelEdit}
        />
      )}
      {addToShortlistPopoverAnchor && (
        <AddProspectsToShortlistPopover
          resultCount={resultCount}
          popoverAnchor={addToShortlistPopoverAnchor}
          onClose={() => setAddToShortlistPopoverAnchor(null)}
          searchBody={searchBody}
        />
      )}
    </>
  )
}

export default memo(ResultChips)
